import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  fetchPrecedentFull,
  runTrial,
  trialFollowup,
  type AdvocateOpinion,
  type FollowupTurn,
  type Jurisdiction,
  type PrecedentDetail,
  type SuggestedLetter,
  type TrialResult,
} from "../lib/api";
import { printVerdict } from "../lib/verdictPrint";

interface Props {
  caseId: string;
  /** Optional vorbereiteter caseContext aus Step 1 — überschreibt Default. */
  initialCaseContext?: string;
  /** Vorberechnetes Trial-Result (Schnellfrage hat es bereits gefahren). */
  initialResult?: TrialResult;
}

export default function B2CAdvisor({ caseId, initialCaseContext, initialResult }: Props) {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<TrialResult | null>(initialResult ?? null);
  const [error, setError] = useState<string | null>(null);
  const [jurisdiction, setJurisdiction] = useState<Jurisdiction>("DE");
  const [caseContext, setCaseContext] = useState(
    initialCaseContext ??
      "Mieter meldet seit 4 Monaten Schimmel im Schlafzimmer (ca. 15 qm). Vermieter reagiert nicht. Monatsmiete 920 EUR. Beweise: Fotos, Mängelanzeige per Einwurf-Einschreiben."
  );

  const run = async () => {
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const r = await runTrial({ caseId, caseContext, jurisdiction });
      setResult(r);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <label className="block text-sm text-slate-300 mb-1">
          Fallbeschreibung <span className="text-slate-500">(Kontext für den LLM-Schwarm)</span>
        </label>
        <textarea
          value={caseContext}
          onChange={(e) => setCaseContext(e.target.value)}
          rows={5}
          className="w-full rounded-lg bg-slate-900 border border-slate-700 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-court-500 font-mono"
        />
      </div>

      <div>
        <label className="block text-sm text-slate-300 mb-1">
          Gerichtsbarkeit{" "}
          <span className="text-slate-500">
            (bestimmt Zusammensetzung der 50er-Jury)
          </span>
        </label>
        <div className="grid grid-cols-4 gap-2">
          {(["DE", "DACH", "EU", "US"] as const).map((j) => (
            <button
              key={j}
              type="button"
              onClick={() => setJurisdiction(j)}
              className={`rounded-lg px-3 py-2 text-sm font-medium border transition ${
                jurisdiction === j
                  ? "bg-court-700/30 border-court-500 text-court-200"
                  : "bg-slate-900 border-slate-700 text-slate-300 hover:border-slate-600"
              }`}
            >
              {j === "DE" && "🇩🇪 Deutschland"}
              {j === "DACH" && "🇦🇹🇨🇭 DACH"}
              {j === "EU" && "🇪🇺 EU-Verbraucher"}
              {j === "US" && "🇺🇸 US Mock Jury"}
            </button>
          ))}
        </div>
        <p className="mt-1 text-xs text-slate-500">
          {jurisdiction === "US"
            ? "12-Geschworenen-Bank + 3 Judges + 7 Plaintiff/7 Defense Counsel."
            : "Richterkollegium + 5 Schöffen + Verbraucher-Juristen + DSGVO-Experten."}
        </p>
      </div>

      <button
        onClick={run}
        disabled={loading || caseContext.length < 20}
        className="w-full rounded-lg bg-gradient-to-r from-court-500 to-court-700 hover:from-court-400 hover:to-court-600 disabled:opacity-50 px-4 py-3 font-semibold transition shadow-lg shadow-court-700/30"
      >
        {loading ? "Schwarm debattiert…" : "🧑‍⚖️ Mock Jury starten"}
      </button>

      {error && (
        <div className="rounded-lg bg-red-950/50 border border-red-800 px-4 py-2 text-sm text-red-200">
          {error}
        </div>
      )}

      {result && (
        <div className="space-y-6">
          {result.advocateOpinion && (
            <AdvocateCard
              opinion={result.advocateOpinion}
              caseContext={caseContext}
            />
          )}
          <VerdictCard result={result} caseContext={caseContext} />
          <FollowupSection caseContext={caseContext} result={result} />
        </div>
      )}
    </div>
  );
}

/**
 * Conversational Follow-up nach dem Verdict — User kann Rückfragen an
 * "Dr. Hartmann" stellen, die mit dem caseContext + Verdict im Kontext
 * beantwortet werden. Stateless: history wird im Browser-State gehalten
 * und bei jedem Roundtrip mitgeschickt.
 */
function FollowupSection({
  caseContext,
  result,
}: {
  caseContext: string;
  result: TrialResult;
}) {
  const [question, setQuestion] = useState("");
  const [history, setHistory] = useState<FollowupTurn[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const ask = async () => {
    const q = question.trim();
    if (q.length < 3) return;
    setLoading(true);
    setError(null);
    try {
      const res = await trialFollowup({
        caseContext,
        question: q,
        winProbability: result.winProbability,
        estimatedDamagesEur: result.estimatedDamagesNumeric,
        recommendation: result.advocateOpinion?.recommendation,
        recommendationReasoning: result.advocateOpinion?.reasoning,
        history,
      });
      setHistory((h) => [
        ...h,
        { role: "user", content: q },
        { role: "assistant", content: res.answer },
      ]);
      setQuestion("");
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="rounded-2xl border border-slate-800 bg-slate-900/40 p-6 space-y-4">
      <div>
        <h3 className="text-base font-semibold text-court-300">
          💬 Rückfrage an Dr. Hartmann
        </h3>
        <p className="text-xs text-slate-500 mt-1">
          Verstehst Du das Verdict nicht? Brauchst Du Hilfe bei einem
          konkreten nächsten Schritt? Frag direkt nach.
        </p>
      </div>

      {history.length > 0 && (
        <div className="space-y-3 max-h-[420px] overflow-auto pr-1">
          {history.map((turn, i) => (
            <div
              key={i}
              className={
                turn.role === "user"
                  ? "rounded-lg bg-slate-950/70 border border-slate-800 px-3 py-2 text-sm text-slate-200 ml-6"
                  : "rounded-lg bg-court-950/40 border border-court-800/60 px-3 py-2 text-sm text-slate-100 mr-6"
              }
            >
              <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">
                {turn.role === "user" ? "Du" : "Dr. Hartmann"}
              </p>
              <p className="whitespace-pre-wrap leading-relaxed">{turn.content}</p>
            </div>
          ))}
        </div>
      )}

      <div className="flex gap-2 items-end">
        <textarea
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
              e.preventDefault();
              ask();
            }
          }}
          rows={2}
          placeholder="z. B. Was wenn der Vermieter weiterhin nicht reagiert? Wie genau formuliere ich die Mängelanzeige?"
          className="flex-1 rounded-lg bg-slate-900 border border-slate-700 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-court-500"
        />
        <button
          type="button"
          onClick={ask}
          disabled={loading || question.trim().length < 3}
          className="shrink-0 rounded-lg bg-court-600 hover:bg-court-500 disabled:opacity-40 px-4 py-2 text-sm font-semibold text-white transition"
        >
          {loading ? "…" : "Fragen"}
        </button>
      </div>
      <p className="text-[10px] text-slate-500">
        Cmd/Ctrl+Enter zum Senden · Antwort kann ~5 Sek. dauern
      </p>

      {error && (
        <div className="rounded-lg bg-red-950/50 border border-red-800 px-3 py-2 text-xs text-red-200">
          {error}
        </div>
      )}
    </div>
  );
}

/**
 * Hero-Karte mit der Plain-Language-Empfehlung des fixen Advocate-Agents.
 * Bewusst ÜBER der 50er-Jury gerendert: B2C-User sollen die Empfehlung sehen
 * bevor sie sich in die 50 Juror-Stimmen stürzen. Die Jury bleibt als
 * Evidenz-Untermauerung darunter sichtbar.
 */
function AdvocateCard({
  opinion,
  caseContext,
}: {
  opinion: AdvocateOpinion;
  caseContext: string;
}) {
  const recStyle =
    opinion.recommendation === "pursue"
      ? {
          badge: "bg-emerald-950 text-emerald-200 border-emerald-700",
          pill: "bg-emerald-500/20 text-emerald-200 border-emerald-600",
          label: "Klagen / Durchsetzen",
          gradient: "from-emerald-600/20",
        }
      : opinion.recommendation === "settle"
        ? {
            badge: "bg-amber-950 text-amber-200 border-amber-700",
            pill: "bg-amber-500/20 text-amber-100 border-amber-600",
            label: "Vergleich anstreben",
            gradient: "from-amber-500/20",
          }
        : {
            badge: "bg-red-950 text-red-200 border-red-700",
            pill: "bg-red-500/20 text-red-200 border-red-600",
            label: "Fall fallenlassen",
            gradient: "from-red-500/20",
          };

  const uncertaintyLabel =
    opinion.uncertainty === "low"
      ? "Prognose: robust"
      : opinion.uncertainty === "medium"
        ? "Prognose: mittlere Unsicherheit"
        : "Prognose: hohe Unsicherheit";
  const uncertaintyColor =
    opinion.uncertainty === "low"
      ? "text-emerald-300"
      : opinion.uncertainty === "medium"
        ? "text-amber-300"
        : "text-red-300";

  const evFormatted = opinion.expectedValueEur.toLocaleString("de-DE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  return (
    <div className="rounded-2xl border border-court-700/60 bg-slate-900/70 overflow-hidden shadow-xl shadow-court-900/30">
      <div
        className={`bg-gradient-to-br ${recStyle.gradient} to-transparent p-6 border-b border-slate-800`}
      >
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <p className="text-[11px] uppercase tracking-wider text-slate-400">
              Empfehlung deines Anwalts
            </p>
            <p className="mt-1 text-lg font-semibold text-slate-100 truncate">
              {opinion.lawyerName}
              <span className="text-slate-400 font-normal">
                {" "}
                · {opinion.lawyerTitle}
              </span>
            </p>
            <p className="text-xs text-slate-500 truncate">
              {opinion.lawyerAffiliation}{" "}
              <span className="text-slate-600">(fiktiver Advisor)</span>
            </p>
          </div>
          <span
            className={`shrink-0 inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-semibold uppercase tracking-wider ${recStyle.pill}`}
          >
            {recStyle.label}
          </span>
        </div>
        <p className="mt-5 text-xl leading-snug font-semibold text-slate-50">
          „{opinion.headline}"
        </p>
      </div>

      <div className="p-6 space-y-5">
        <section>
          <h3 className="text-sm font-semibold text-court-300 mb-2">
            Einschätzung
          </h3>
          <p className="text-slate-200 leading-relaxed text-sm">
            {opinion.reasoning}
          </p>
        </section>

        <section>
          <h3 className="text-sm font-semibold text-court-300 mb-2">
            Nächste Schritte
          </h3>
          <ol className="space-y-2 list-none">
            {opinion.nextSteps.map((step, i) => (
              <li
                key={i}
                className="flex gap-3 text-sm text-slate-200 rounded-lg bg-slate-950/60 border border-slate-800 px-3 py-2"
              >
                <span className="shrink-0 font-mono text-xs text-court-400 pt-0.5">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className="leading-snug">{step}</span>
              </li>
            ))}
          </ol>
        </section>

        {opinion.suggestedLetter && (
          <SuggestedLetterCTA
            letter={opinion.suggestedLetter}
            caseContext={caseContext}
            recipientFallback={opinion.suggestedLetter.recipient}
          />
        )}

        <section className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="rounded-lg bg-slate-950/70 border border-slate-800 px-4 py-3">
            <p className="text-[11px] uppercase tracking-wider text-slate-500">
              Erwartungswert
            </p>
            <p
              className={`mt-1 font-mono text-lg ${
                opinion.expectedValueEur > 0
                  ? "text-emerald-300"
                  : "text-red-300"
              }`}
            >
              {evFormatted} EUR
            </p>
            <p className="text-[10px] text-slate-600 mt-1">
              nach Anwalts-/Prozesskosten
            </p>
          </div>
          <div className="rounded-lg bg-slate-950/70 border border-slate-800 px-4 py-3">
            <p className="text-[11px] uppercase tracking-wider text-slate-500">
              Echten Anwalt einschalten?
            </p>
            <p className="mt-1 text-lg font-semibold">
              {opinion.lohntAnwalt ? (
                <span className="text-emerald-300">Ja, lohnt sich</span>
              ) : (
                <span className="text-slate-300">Nein, solo machbar</span>
              )}
            </p>
            <p className="text-[10px] text-slate-600 mt-1">
              Faustregel ab ~750 EUR Streitwert
            </p>
          </div>
          <div className="rounded-lg bg-slate-950/70 border border-slate-800 px-4 py-3">
            <p className="text-[11px] uppercase tracking-wider text-slate-500">
              Zuverlässigkeit
            </p>
            <p className={`mt-1 text-lg font-semibold ${uncertaintyColor}`}>
              {uncertaintyLabel}
            </p>
            <p className="text-[10px] text-slate-600 mt-1">
              basiert auf Jury-Streubreite
            </p>
          </div>
        </section>

        <p className="text-[11px] text-slate-500 leading-relaxed border-t border-slate-800 pt-3">
          Hinweis: Dies ist eine automatisierte Orientierung auf Basis einer
          Simulation fiktiver Juroren — keine verbindliche Rechtsberatung und
          kein Mandatsverhältnis im Sinne § 3 RDG. Für bindende rechtliche
          Schritte konsultiere bitte einen zugelassenen Rechtsanwalt.
        </p>
      </div>
    </div>
  );
}

function VerdictCard({
  result,
  caseContext,
}: {
  result: TrialResult;
  caseContext: string;
}) {
  const p = result.winProbability;
  const pColor =
    p >= 70 ? "text-emerald-400" : p >= 40 ? "text-amber-300" : "text-red-400";
  const pBg =
    p >= 70 ? "from-emerald-500/20" : p >= 40 ? "from-amber-500/20" : "from-red-500/20";

  // Aggregate jury stats — bei 50 Juroren ist die Einzelliste zu lang fürs Auge,
  // also: zuerst Verteilung zeigen, dann (collapsed) Einzelmeinungen.
  const stats = useMemo(() => {
    let fav = 0, neu = 0, unf = 0;
    for (const v of result.swarmVerdicts) {
      if (v.verdict === "favorable") fav++;
      else if (v.verdict === "neutral") neu++;
      else unf++;
    }
    const n = result.swarmVerdicts.length || 1;
    return {
      fav, neu, unf,
      favPct: Math.round((fav / n) * 100),
      neuPct: Math.round((neu / n) * 100),
      unfPct: Math.round((unf / n) * 100),
      total: result.swarmVerdicts.length,
    };
  }, [result.swarmVerdicts]);

  // Top-K nach Confidence — für die initial sichtbare Liste.
  const sortedVerdicts = useMemo(
    () => [...result.swarmVerdicts].sort((a, b) => b.confidence - a.confidence),
    [result.swarmVerdicts]
  );
  const [showAll, setShowAll] = useState(false);
  const visibleVerdicts = showAll ? sortedVerdicts : sortedVerdicts.slice(0, 8);

  return (
    <div className="rounded-2xl border border-slate-700 bg-slate-900/60 overflow-hidden">
      {/* Hero */}
      <div className={`bg-gradient-to-br ${pBg} to-transparent p-8 border-b border-slate-800`}>
        <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
          {result.jurisdiction ? (
            <div className="inline-flex items-center gap-2 rounded-full bg-slate-950/60 border border-slate-800 px-3 py-1 text-[11px] uppercase tracking-wider text-slate-300">
              <span>🧑‍⚖️ Gerichtsbarkeit</span>
              <span className="font-mono text-court-300">{result.jurisdiction}</span>
            </div>
          ) : (
            <span />
          )}
          <button
            type="button"
            onClick={() => printVerdict(result, caseContext)}
            title={'Druckbare Ansicht öffnen — im Druck-Dialog „Speichern als PDF" wählen'}
            className="inline-flex items-center gap-2 rounded-lg border border-slate-700 hover:border-court-500 hover:bg-slate-950/60 px-3 py-1.5 text-xs font-semibold text-slate-200 hover:text-white transition"
          >
            📄 Verdict als PDF
          </button>
        </div>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs uppercase tracking-wider text-slate-400">Gewinnwahrscheinlichkeit</p>
            <div className={`text-6xl font-bold ${pColor} font-mono`}>{p}%</div>
          </div>
          <div className="text-right">
            <p className="text-xs uppercase tracking-wider text-slate-400">Geschätzter Schadensersatz</p>
            <div className="text-3xl font-semibold">{result.estimatedDamages}</div>
            <p className="mt-2 text-xs uppercase tracking-wider text-slate-400">Vergleichsempfehlung</p>
            <div className="text-xl font-semibold text-court-300">
              {result.settlementRecommendation.toFixed(2)} EUR
            </div>
          </div>
        </div>
      </div>

      <div className="p-6 space-y-6">
        <section>
          <h3 className="text-sm font-semibold text-court-300 mb-2">Strategie</h3>
          <p className="text-slate-200 leading-relaxed">{result.battlePlan}</p>
        </section>

        <section>
          <h3 className="text-sm font-semibold text-court-300 mb-3">
            Schwarm-Urteile ({stats.total} Juroren)
          </h3>

          {/* Verteilung als Balken — gibt sofort einen Eindruck */}
          <div className="mb-4 rounded-lg bg-slate-950/70 border border-slate-800 p-3">
            <div className="flex h-3 w-full overflow-hidden rounded-full bg-slate-800">
              {stats.fav > 0 && (
                <div
                  className="bg-emerald-500"
                  style={{ width: `${stats.favPct}%` }}
                  title={`${stats.fav} pro Mandant (${stats.favPct}%)`}
                />
              )}
              {stats.neu > 0 && (
                <div
                  className="bg-amber-500"
                  style={{ width: `${stats.neuPct}%` }}
                  title={`${stats.neu} neutral (${stats.neuPct}%)`}
                />
              )}
              {stats.unf > 0 && (
                <div
                  className="bg-red-500"
                  style={{ width: `${stats.unfPct}%` }}
                  title={`${stats.unf} contra (${stats.unfPct}%)`}
                />
              )}
            </div>
            <div className="mt-3 flex justify-between text-xs">
              <span className="text-emerald-300">
                <span className="font-mono font-semibold">{stats.fav}</span> pro Mandant ({stats.favPct}%)
              </span>
              <span className="text-amber-300">
                <span className="font-mono font-semibold">{stats.neu}</span> neutral ({stats.neuPct}%)
              </span>
              <span className="text-red-300">
                <span className="font-mono font-semibold">{stats.unf}</span> contra ({stats.unfPct}%)
              </span>
            </div>
          </div>

          {/* Sprint I/5: Per-Juror Dot-Grid — jeder Punkt = ein Juror, Farbe = Verdict, Sättigung = Confidence */}
          <SwarmDotGrid verdicts={sortedVerdicts} />

          <div className="space-y-2">
            {visibleVerdicts.map((v, i) => (
              <div
                key={`${v.jurorId ?? v.model}-${i}`}
                className="flex items-start gap-3 rounded-lg bg-slate-950/70 border border-slate-800 px-4 py-3"
              >
                <VerdictBadge verdict={v.verdict} />
                <div className="flex-1 min-w-0">
                  {v.name ? (
                    <>
                      <p className="text-sm font-semibold text-slate-100 truncate">
                        {v.name}
                        {v.title && (
                          <span className="font-normal text-slate-400">
                            {" "}
                            · {v.title}
                          </span>
                        )}
                      </p>
                      {v.affiliation && (
                        <p className="text-xs text-slate-500 truncate">
                          {v.affiliation}{" "}
                          <span className="text-slate-600">(fiktiv)</span>
                        </p>
                      )}
                    </>
                  ) : (
                    <p className="text-xs font-mono text-slate-400">{v.model}</p>
                  )}
                  <p className="text-sm text-slate-200 mt-1">{v.reasoning}</p>
                  <p className="mt-1 font-mono text-[10px] text-slate-600 truncate">
                    {v.role && <span>Rolle: {v.role}</span>}
                    {v.role && v.persona && " · "}
                    {v.persona && <span>{v.persona}</span>}
                    {(v.role || v.persona) && " · "}
                    <span>{v.model}</span>
                  </p>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-xs text-slate-500">Confidence</p>
                  <p className="font-mono text-sm">{Math.round(v.confidence * 100)}%</p>
                </div>
              </div>
            ))}
          </div>

          {sortedVerdicts.length > 8 && (
            <button
              type="button"
              onClick={() => setShowAll((s) => !s)}
              className="mt-3 w-full rounded-lg border border-slate-700 bg-slate-950/40 px-3 py-2 text-xs text-court-300 hover:bg-slate-900 hover:border-court-700 transition"
            >
              {showAll
                ? `↑ Weniger anzeigen`
                : `↓ Alle ${sortedVerdicts.length} Juroren anzeigen (${sortedVerdicts.length - 8} weitere)`}
            </button>
          )}
        </section>

        <PrecedentsSection precedents={result.precedentsUsed} />
      </div>
    </div>
  );
}

/**
 * Sprint I/3 (2026-04-29): Klickbare Präzedenzfall-Liste mit Modal.
 *
 * Pro Eintrag wird der KEY (citation) verwendet — bei Bundesrecht-Bulk-
 * Ingest können verschiedene Vektoren auf dieselbe Citation matchen, deshalb
 * fallback-key über (citation + index).
 *
 * Modal zeigt die volle Leitsatz-Snippet (bis 1200 Zeichen aus Vectorize-
 * Metadata), Themen-Tags, Normen-Liste und einen "Volltext öffnen"-Link auf
 * die amtliche Quelle. Modal wird mit Esc + Klick außerhalb geschlossen.
 */
function PrecedentsSection({
  precedents,
}: {
  precedents: TrialResult["precedentsUsed"];
}) {
  const [active, setActive] = useState<TrialResult["precedentsUsed"][number] | null>(
    null
  );

  return (
    <section>
      <h3 className="text-sm font-semibold text-court-300 mb-3">
        Präzedenzfälle ({precedents.length})
        {precedents.length > 0 && (
          <span className="ml-2 text-[11px] font-normal text-slate-500">
            · klick für Volltext
          </span>
        )}
      </h3>
      <div className="space-y-2">
        {precedents.length === 0 ? (
          <p className="text-sm text-slate-500 italic">
            Keine direkten Präzedenzfälle gefunden.
          </p>
        ) : (
          precedents.map((p, i) => (
            <button
              key={`${p.citation}-${i}`}
              type="button"
              onClick={() => setActive(p)}
              className="w-full text-left rounded-lg bg-slate-950/70 border border-slate-800 hover:border-court-700 hover:bg-slate-950 px-4 py-2 text-sm transition group"
            >
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <span className="font-mono text-court-300 group-hover:text-court-200">
                  {p.citation}
                </span>
                <span className="text-xs text-slate-500 shrink-0">
                  Ähnlichkeit {Math.round(p.relevance * 100)}%
                  <span className="ml-2 text-court-400/70 opacity-0 group-hover:opacity-100 transition">
                    Volltext öffnen →
                  </span>
                </span>
              </div>
              <p className="text-slate-400 mt-1 line-clamp-2">{p.snippet}</p>
              {(p.topics?.length || p.norms?.length) && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {p.topics?.slice(0, 3).map((t) => (
                    <span
                      key={`t-${t}`}
                      className="rounded-full border border-slate-800 bg-slate-900/80 px-2 py-0.5 text-[10px] text-slate-400"
                    >
                      {t}
                    </span>
                  ))}
                  {p.norms?.slice(0, 3).map((n) => (
                    <span
                      key={`n-${n}`}
                      className="rounded-full border border-court-800/60 bg-court-950/40 px-2 py-0.5 text-[10px] text-court-300/90 font-mono"
                    >
                      {n}
                    </span>
                  ))}
                </div>
              )}
            </button>
          ))
        )}
      </div>
      {precedents.length > 0 && (
        <p className="mt-3 text-[11px] text-slate-500 leading-relaxed">
          Urteilsdaten teilweise via{" "}
          <a
            href="https://de.openlegaldata.io"
            target="_blank"
            rel="noopener noreferrer"
            className="underline hover:text-slate-400"
          >
            Open Legal Data e.V.
          </a>{" "}
          (ODbL v1.0).
        </p>
      )}
      {active && (
        <PrecedentModal precedent={active} onClose={() => setActive(null)} />
      )}
    </section>
  );
}

function PrecedentModal({
  precedent,
  onClose,
}: {
  precedent: TrialResult["precedentsUsed"][number];
  onClose: () => void;
}) {
  // Esc-to-close + Body-Scroll-Lock — Modal-Standardpflege.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  // Sprint I/3-Fix: Volltext-Nachladung via /api/precedents/:id/full
  // — solange nicht geladen, zeigen wir nur den Snippet (~1700 Zeichen).
  // Auto-Load bei OLD-Records weil dort der snippet bewusst kurz ist;
  // bei curated landmarks erst auf User-Klick weil snippet dort schon
  // den vollständigen kuratierten Leitsatz enthält.
  const [fullDetail, setFullDetail] = useState<PrecedentDetail | null>(null);
  const [fullLoading, setFullLoading] = useState(false);
  const [fullError, setFullError] = useState<string | null>(null);

  const canLoadFull =
    precedent.vectorId &&
    (precedent.vectorId.startsWith("prec-old:") || precedent.vectorId.startsWith("prec:"));

  const loadFull = async () => {
    if (!precedent.vectorId || fullLoading) return;
    setFullLoading(true);
    setFullError(null);
    try {
      const detail = await fetchPrecedentFull(precedent.vectorId);
      setFullDetail(detail);
    } catch (err) {
      setFullError(err instanceof Error ? err.message : String(err));
    } finally {
      setFullLoading(false);
    }
  };

  // Auto-load für OLD-Records — der Snippet ist da bewusst nur ein Auszug,
  // also direkt das volle Urteil holen sobald das Modal aufgeht.
  useEffect(() => {
    if (precedent.source === "open_legal_data" && !fullDetail && !fullLoading && !fullError) {
      loadFull();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [precedent.vectorId]);

  const headline =
    precedent.name ??
    [precedent.court, precedent.az].filter(Boolean).join(" ") ??
    precedent.citation;

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm p-3 sm:p-6"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-2xl max-h-[90vh] overflow-hidden rounded-2xl border border-court-700/60 bg-slate-950 shadow-2xl shadow-black/50 flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="px-6 py-5 border-b border-slate-800 bg-gradient-to-br from-court-900/40 to-transparent">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-[11px] uppercase tracking-wider text-court-300 font-semibold">
                Präzedenzfall · Volltext
              </p>
              <h3 className="mt-1 text-lg font-semibold text-slate-100 leading-snug">
                {headline}
              </h3>
              <p className="mt-1 font-mono text-xs text-slate-400">
                {[precedent.court, precedent.az, precedent.date]
                  .filter(Boolean)
                  .join(" · ")}
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label="Schließen"
              className="shrink-0 rounded-lg border border-slate-700 hover:border-slate-500 hover:bg-slate-900/80 w-8 h-8 inline-flex items-center justify-center text-slate-400 hover:text-white transition"
            >
              ✕
            </button>
          </div>
          <p className="mt-3 text-[11px] text-slate-500">
            Cosine-Ähnlichkeit zur Anfrage:{" "}
            <span className="font-mono text-court-300">
              {Math.round(precedent.relevance * 100)}%
            </span>
          </p>
        </header>

        <div className="px-6 py-5 overflow-auto space-y-4 text-sm">
          {(precedent.topics?.length || precedent.norms?.length) && (
            <section className="space-y-2">
              {precedent.topics?.length ? (
                <div>
                  <p className="text-[11px] uppercase tracking-wider text-slate-500 mb-1">
                    Themen
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {precedent.topics.map((t) => (
                      <span
                        key={`mt-${t}`}
                        className="rounded-full border border-slate-800 bg-slate-900/80 px-2 py-0.5 text-[11px] text-slate-300"
                      >
                        {t}
                      </span>
                    ))}
                  </div>
                </div>
              ) : null}
              {precedent.norms?.length ? (
                <div>
                  <p className="text-[11px] uppercase tracking-wider text-slate-500 mb-1">
                    Normen
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {precedent.norms.map((n) => (
                      <span
                        key={`mn-${n}`}
                        className="rounded-full border border-court-800/60 bg-court-950/40 px-2 py-0.5 text-[11px] text-court-200 font-mono"
                      >
                        {n}
                      </span>
                    ))}
                  </div>
                </div>
              ) : null}
            </section>
          )}

          <section>
            <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
              <p className="text-[11px] uppercase tracking-wider text-slate-500">
                {fullDetail
                  ? `Volltext (${fullDetail.charCount.toLocaleString("de-DE")} Zeichen)`
                  : `Leitsatz ${precedent.snippet.includes("Tenor:") ? "& Tenor" : ""}`}
              </p>
              {canLoadFull && !fullDetail && precedent.source !== "open_legal_data" && (
                <button
                  type="button"
                  onClick={loadFull}
                  disabled={fullLoading}
                  className="inline-flex items-center gap-1 rounded-md border border-court-700/60 hover:border-court-500 hover:bg-court-900/40 px-2 py-1 text-[11px] font-semibold text-court-200 transition disabled:opacity-50"
                >
                  {fullLoading ? "Lädt…" : "📖 Volltext laden"}
                </button>
              )}
            </div>
            <div className="rounded-lg border border-slate-800 bg-slate-900/40 px-4 py-3 max-h-[55vh] overflow-auto">
              {fullLoading && !fullDetail ? (
                <div className="text-sm text-slate-500 italic flex items-center gap-2">
                  <span className="inline-block w-3 h-3 border-2 border-court-500 border-t-transparent rounded-full animate-spin" />
                  Volltext wird live von der Quelle geladen…
                </div>
              ) : fullError ? (
                <div className="text-sm text-red-300">
                  Konnte den Volltext nicht laden: {fullError}
                  <button
                    type="button"
                    onClick={loadFull}
                    className="ml-2 underline hover:text-red-200"
                  >
                    Erneut versuchen
                  </button>
                </div>
              ) : (
                <p className="whitespace-pre-wrap leading-relaxed text-slate-200 text-sm">
                  {fullDetail?.fullText || precedent.snippet || "(Kein Leitsatz im Index hinterlegt.)"}
                </p>
              )}
            </div>
          </section>

          <section className="rounded-lg border border-slate-800/60 bg-slate-900/30 px-4 py-3 text-[11px] text-slate-500 leading-relaxed">
            {precedent.source === "open_legal_data" ? (
              <>
                Quelle: <strong>Open Legal Data e.V.</strong> (ODbL v1.0). Der Volltext wird
                live von der amtlichen Quelle geladen — keine lokale Kopie. Für rechtliche
                Bewertungen bitte das Urteil im Kontext (vollständiger Spruchkörper-Text,
                anhängige Verfahren) prüfen.
              </>
            ) : (
              <>
                Hinweis: Der hier gezeigte Text ist der kuratierte Leitsatz + Tenor-Auszug.
                Für rechtliche Bewertungen bitte den vollständigen Urteilstext über den
                Quellen-Link unten konsultieren — der Leitsatz ist eine verkürzte Form.
              </>
            )}
          </section>
        </div>

        <footer className="border-t border-slate-800 px-6 py-4 flex items-center justify-between gap-3 flex-wrap">
          {precedent.sourceUrl ? (
            <a
              href={precedent.sourceUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-lg bg-court-600 hover:bg-court-500 px-4 py-2 text-sm font-semibold text-white shadow-md transition"
            >
              📖 Volltext bei der Quelle öffnen
              <span className="text-xs opacity-70">↗</span>
            </a>
          ) : (
            <span className="text-xs text-slate-500 italic">
              Keine externe Quellen-URL hinterlegt.
            </span>
          )}
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-slate-700 hover:border-slate-500 px-4 py-2 text-sm text-slate-300 hover:text-white transition"
          >
            Schließen
          </button>
        </footer>
      </div>
    </div>
  );
}

/**
 * Sprint I/2 (2026-04-29): Inline-CTA für Auto-Brief-Vorschlag.
 *
 * Sitzt direkt zwischen "Nächste Schritte" und dem Kennzahlen-Grid in der
 * AdvocateCard. Wenn die LLM- (oder Heuristik-) Logik einen passenden
 * Brief identifiziert hat (suggestedLetter !== undefined), bekommt der
 * User EINEN Klick zur LetterPage mit Pre-Fill — die wichtigste Conversion-
 * Brücke zwischen "Verdict gelesen" und "Brief verschickt".
 *
 * Pre-Fill läuft via react-router-`Link state`, das LetterDrafter über
 * useLocation().state ausliest. Kein URL-Param, kein localStorage —
 * Cookies/Storage bleiben sauber, und der Pre-Fill ist auch nicht via
 * Bookmark wiederherstellbar (was hier wünschenswert ist).
 */
function SuggestedLetterCTA({
  letter,
  caseContext,
  recipientFallback,
}: {
  letter: SuggestedLetter;
  caseContext: string;
  recipientFallback?: string;
}) {
  return (
    <section className="rounded-xl border border-court-600/60 bg-gradient-to-br from-court-900/40 via-slate-900/40 to-slate-900/40 p-5">
      <div className="flex items-start gap-4 flex-wrap">
        <div className="text-3xl leading-none">📨</div>
        <div className="flex-1 min-w-[14rem]">
          <p className="text-[11px] uppercase tracking-wider text-court-300 font-semibold">
            Direkt umsetzen
          </p>
          <p className="mt-1 text-base font-semibold text-slate-100">
            {letter.headline}
          </p>
          {letter.recipient && (
            <p className="text-xs text-slate-400 mt-1">
              Empfänger: <span className="text-slate-200">{letter.recipient}</span>
            </p>
          )}
          {letter.reason && (
            <p className="text-sm text-slate-300 mt-2 leading-relaxed">
              {letter.reason}
            </p>
          )}
        </div>
        <Link
          to="/app/letter"
          state={{
            prefill: {
              letterType: letter.type,
              caseContext,
              recipientName: letter.recipient ?? recipientFallback ?? "",
            },
          }}
          className="shrink-0 inline-flex items-center gap-2 rounded-lg bg-court-600 hover:bg-court-500 px-4 py-2 text-sm font-semibold text-white shadow-md shadow-court-900/40 transition"
        >
          ✏️ Brief jetzt entwerfen
          <span className="text-xs opacity-70">(DOCX)</span>
        </Link>
      </div>
    </section>
  );
}

/**
 * Sprint I/5 (2026-04-29): Schwarm-Stimmen-Visualisierung als Dot-Grid.
 *
 * Jeder Juror = ein Kreis. Farbe codiert das Verdict (emerald/amber/red),
 * Opacity codiert die Confidence (0.35 + conf * 0.65 — Mindestkontrast
 * damit auch 5%-Confidence-Stimmen sichtbar bleiben). Klick auf einen
 * Punkt zeigt Tooltip mit Name + Begründung.
 *
 * Sortierung: pro-Mandant zuerst, dann neutral, dann contra. Innerhalb
 * jeder Gruppe nach Confidence absteigend. So entsteht ein visueller
 * "Schwarm-Verlauf" von Grün → Gelb → Rot der das Stimmungsbild auf
 * einen Blick erfassbar macht.
 */
function SwarmDotGrid({
  verdicts,
}: {
  verdicts: TrialResult["swarmVerdicts"];
}) {
  const [active, setActive] = useState<number | null>(null);

  const ordered = useMemo(() => {
    const rank = (v: "favorable" | "neutral" | "unfavorable") =>
      v === "favorable" ? 0 : v === "neutral" ? 1 : 2;
    return [...verdicts].sort((a, b) => {
      if (rank(a.verdict) !== rank(b.verdict)) return rank(a.verdict) - rank(b.verdict);
      return b.confidence - a.confidence;
    });
  }, [verdicts]);

  // Adaptive Spaltenzahl: bei >40 Stimmen 10er-Reihen, sonst 8er.
  // Hält die Höhe der Visualisierung in einem komfortablen Rahmen ohne
  // dass einzelne Punkte zu winzig werden.
  const cols = ordered.length > 40 ? 10 : 8;
  const dotSize = 28;
  const gap = 6;
  const rows = Math.ceil(ordered.length / cols);
  const width = cols * dotSize + (cols - 1) * gap;
  const height = rows * dotSize + (rows - 1) * gap;

  const colorFor = (v: "favorable" | "neutral" | "unfavorable") =>
    v === "favorable" ? "#10b981" : v === "neutral" ? "#f59e0b" : "#ef4444";

  if (ordered.length === 0) return null;

  const activeJuror = active !== null ? ordered[active] : null;

  return (
    <div className="mb-4 rounded-lg bg-slate-950/70 border border-slate-800 p-4">
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <p className="text-xs uppercase tracking-wider text-slate-400">
          Schwarm-Verteilung · jeder Punkt = ein Juror
        </p>
        <p className="text-[10px] text-slate-500">
          Helligkeit = Confidence · Klick für Details
        </p>
      </div>
      <div className="relative">
        <svg
          viewBox={`0 0 ${width} ${height}`}
          width="100%"
          height="auto"
          style={{ maxHeight: 260 }}
          role="img"
          aria-label={`${ordered.length} Juror-Stimmen visualisiert als Punkt-Grid`}
        >
          {ordered.map((v, i) => {
            const col = i % cols;
            const row = Math.floor(i / cols);
            const cx = col * (dotSize + gap) + dotSize / 2;
            const cy = row * (dotSize + gap) + dotSize / 2;
            // Confidence-Mapping: 0..1 → opacity 0.35..1.0 (Mindestkontrast)
            const opacity = 0.35 + Math.max(0, Math.min(1, v.confidence)) * 0.65;
            const isActive = active === i;
            return (
              <g key={`${v.jurorId ?? v.model}-${i}`}>
                <circle
                  cx={cx}
                  cy={cy}
                  r={dotSize / 2 - 1}
                  fill={colorFor(v.verdict)}
                  fillOpacity={opacity}
                  stroke={isActive ? "#fff" : "rgba(15,23,42,0.6)"}
                  strokeWidth={isActive ? 2 : 1}
                  style={{ cursor: "pointer", transition: "stroke-width 120ms" }}
                  onClick={() => setActive(isActive ? null : i)}
                >
                  <title>
                    {v.name ?? v.model} — {v.verdict} ({Math.round(v.confidence * 100)}%)
                  </title>
                </circle>
              </g>
            );
          })}
        </svg>
      </div>
      {activeJuror && (
        <div className="mt-3 rounded-lg border border-slate-700 bg-slate-900/80 px-4 py-3 text-sm">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <p className="font-semibold text-slate-100">
                {activeJuror.name ?? activeJuror.model}
                {activeJuror.title && (
                  <span className="font-normal text-slate-400"> · {activeJuror.title}</span>
                )}
              </p>
              {activeJuror.affiliation && (
                <p className="text-xs text-slate-500">
                  {activeJuror.affiliation} <span className="text-slate-600">(fiktiv)</span>
                </p>
              )}
              <p className="mt-2 text-slate-200 leading-relaxed">{activeJuror.reasoning}</p>
              <p className="mt-2 text-[10px] font-mono text-slate-500">
                {activeJuror.verdict} · Confidence {Math.round(activeJuror.confidence * 100)}% · {activeJuror.model}
              </p>
            </div>
            <button
              type="button"
              onClick={() => setActive(null)}
              className="shrink-0 text-slate-500 hover:text-white text-sm w-6 h-6 inline-flex items-center justify-center rounded border border-slate-700 hover:border-slate-500 transition"
              aria-label="Tooltip schließen"
            >
              ✕
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function VerdictBadge({ verdict }: { verdict: "favorable" | "neutral" | "unfavorable" }) {
  const label = verdict === "favorable" ? "✓" : verdict === "neutral" ? "~" : "✗";
  const cls =
    verdict === "favorable"
      ? "bg-emerald-950 text-emerald-300 border-emerald-700"
      : verdict === "neutral"
      ? "bg-amber-950 text-amber-300 border-amber-700"
      : "bg-red-950 text-red-300 border-red-700";
  return (
    <span
      className={`inline-flex items-center justify-center shrink-0 w-8 h-8 rounded-full border font-bold ${cls}`}
    >
      {label}
    </span>
  );
}
